import { IAssistantMessage, IQuestion, IQuestionOption } from '../types';
import { CILL_HORN_MIN_FOR_ANGLED, OPTION_TYPE_ANGLED_CILL_HORN, OPTION_TYPE_CILL_HORN_SIZE, OPTION_TYPE_COUPLING_CLIP, OPTION_TYPE_DHANDLE_POSITION, OPTION_TYPE_FIXING_LUGS, OPTION_TYPE_FITTING_TYPE, OPTION_TYPE_GLASS_SPECIFICATION, OPTION_TYPE_HARDWARE_ITEM, OPTION_TYPE_HARDWARE_REQUIRED, OPTION_TYPE_HARDWARE_TYPE, OPTION_TYPE_SPRAY, OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE, PROFILE_COLOR_OPTION, ProductStructureType } from "../constants";
import {
  ARCH_FOIL_COLOUR_WIDTH_MIN,
  isProfileColourFoilOption,
  isSweptArchProduct,
  shouldShowArchProfileColourOption,
  shouldShowSweptArchFoilOption,
} from './profileColour';


/**
 * optionUnit values (kept inline to avoid a cross-import for a tiny mapping):
 *   1 = Per Window, 2 = Per Sash, 3 = Per Meter, 4 = Per Unit
 * Per-sash and per-meter each need dynamic scaling.
 */
const PER_SASH_UNIT = 2;
const PER_METER_UNIT = 3;
const PER_FRAME_UNIT = 5;
// Width of a single coupling clip in mm. Coupled windows deduct (frameCount-1) × this
// from the overall width before computing per-metre add-on prices.
const COUPLING_CLIP_WIDTH_MM = 6;
const PLACEMENT_VALUES = new Set(['top', 'bottom', 'both']);

/**
 * Merge every user message's form values into a single flat record keyed by
 * responseValue. Used to resolve per-metre lengths at render time without
 * having to re-traverse messages inside the display pipeline.
 */
export const getFormValuesFromMessages = (
  messages: IAssistantMessage[] = [],
): Record<string, unknown> => {
  const merged: Record<string, unknown> = {};
  for (const msg of messages) {
    if (msg.type !== 'user') continue;
    const form = msg.response?.form;
    if (!form) continue;
    for (const [key, value] of Object.entries(form)) {
      if (value !== undefined && value !== null && value !== '') {
        merged[key] = value;
      }
    }
  }
  return merged;
};

/**
 * Re-apply render-time transformations (priceList resolution + per-sash /
 * per-metre multipliers) to every user message's stored question + answer
 * using the latest merged answers, form values, and selected product.
 *
 * Needed because unit multipliers are pre-baked into `priceModifier`/`price`/
 * `vssPrice` when an option is first rendered. If the user later edits a
 * dimension or product-level field that doesn't formally depend on a per-metre
 * / per-sash question (so the drawer doesn't re-ask it), those baked prices
 * would otherwise stay stale. Call this on drawer save / cart commit.
 */
export const rebakeMessagesWithLatestState = (
  messages: IAssistantMessage[] = [],
  questionFlow: IQuestion[] = [],
  selectedProduct: IQuestionOption,
): IAssistantMessage[] => {
  if (!messages.length) return messages;

  const answers: Record<string, IQuestionOption[]> = {};

  for (const msg of messages) {
    if (msg.type !== "user") continue;
    const catId = msg.question?.category;
    if (catId && msg.response?.answer?.length) {
      if (!answers[catId]) answers[catId] = [];
      answers[catId].push(...msg.response.answer);
    }
  }

  const formValues = getFormValuesFromMessages(messages);

  // Build maps from questionFlow for re-hydration and base-price reset.
  // Cart-stored messages have their question.options stripped by slimMessages
  // (to stay within localStorage quota), so we re-hydrate from questionFlow here.
  const flowOptionMap = new Map<string, IQuestionOption>();
  const flowQuestionMap = new Map<string, IQuestion>();
  for (const q of questionFlow) {
    if (q.questionId) flowQuestionMap.set(q.questionId, q);
    for (const opt of q.options ?? []) {
      if (opt.optionId) flowOptionMap.set(opt.optionId, opt);
    }
  }

  return messages.map((msg) => {
    if (msg.type !== "user") return msg;
    // Synthetic coupling clip messages carry pre-calculated prices; skip rebaking
    // so resolveOptionUnitMultiplier doesn't multiply by (frameCount-1) again.
    if (msg.question?.optionType === OPTION_TYPE_COUPLING_CLIP) return msg;

    // Re-hydrate stripped options: cart-stored messages have options removed by
    // slimMessages to save localStorage quota. Look up the full question from
    // questionFlow so the rebake can access all options and their base prices.
    const fullQuestion: IQuestion = msg.question?.options?.length
      ? msg.question
      : (flowQuestionMap.get(msg.question?.questionId ?? "") ?? msg.question);

    if (!fullQuestion?.options?.length) return msg;

    // Reset option prices to base config values before re-baking so that options
    // without a priceList (direct priceModifier) are not double-multiplied.
    const questionWithBasePrices: IQuestion =
      flowOptionMap.size > 0
        ? {
            ...fullQuestion,
            options: (fullQuestion.options ?? []).map((o) => {
              const base = flowOptionMap.get(o.optionId);
              if (!base) return o;
              return {
                ...o,
                price: base.price,
                priceModifier: base.priceModifier,
                vssPrice: base.vssPrice ?? 0,
              };
            }),
          }
        : fullQuestion;

    const refiltered = filterQuestionOptions(
      questionWithBasePrices,
      answers,
      selectedProduct,
      formValues,
    );
    const byId = new Map(
      (refiltered.options ?? []).map((o) => [o.optionId, o]),
    );
    const updatedAnswer = (msg.response?.answer ?? []).map(
      (a) => byId.get(a.optionId) ?? a,
    );
    return {
      ...msg,
      question: refiltered,
      response: {
        ...msg.response,
        answer: updatedAnswer,
      },
    };
  });
};

/**
 * Get all valid product IDs from previous answers
 * Looks for options with validProducts array in their responses
 */
const getValidProductsFromAnswers = (
  answers: Record<string, IQuestionOption[]>,
): string[] => {
  const allValidProducts: string[] = [];

  Object.values(answers).forEach((answerOptions) => {
    answerOptions.forEach((option) => {
      if (option.validProducts && option.validProducts.length > 0) {
        allValidProducts.push(...option.validProducts);
      }
    });
  });

  return [...new Set(allValidProducts)]; // Remove duplicates
};

/**
 * Check if there's an intersection between two arrays
 */
const hasIntersection = (arr1: string[], arr2: string[]): boolean => {
  return arr1.some((item) => arr2.includes(item));
};

/**
 * Collects all product optionIds relevant to the current context:
 * the explicit selectedProduct + any WINDOW_TYPE or VARIATION options
 * recorded in answers that share the SAME producttypecode as selectedProduct.
 *
 * Restricting to the same producttypecode ensures that validForProducts checks
 * succeed when the admin stored the UUID against the WINDOW_TYPE selection
 * (made first in the flow) rather than the VARIATION selection (made later),
 * while preventing stale answers for a previously-selected product type
 * (e.g. old Arch UUID remaining in answers after switching to Standard) from
 * accidentally making product-specific options visible.
 */
const collectAllProductIds = (
  selectedProduct: IQuestionOption | undefined,
  answers: Record<string, IQuestionOption[]>,
): string[] => {
  const ids = new Set<string>();
  if (!selectedProduct) return [];
  ids.add(selectedProduct.optionId);
  // Only collect sibling UUIDs that share the same product-type code so that
  // a stale answer for a different product family never widens the allowlist.
  if (selectedProduct.producttypecode !== undefined) {
    Object.values(answers).forEach(opts =>
      opts.forEach(opt => {
        if (
          (opt.optionType === OPTION_TYPE_WINDOW_TYPE || opt.optionType === OPTION_TYPE_VARIATION) &&
          opt.optionId && opt.optionId.length > 7 && !opt.optionId.includes('OPTION') &&
          opt.producttypecode === selectedProduct.producttypecode
        ) {
          ids.add(opt.optionId);
        }
      })
    );
  }
  return Array.from(ids);
};

/**
 * Extract answers from message history, grouped by category.
 */
export const getAnswersFromMessages = (
  messages: IAssistantMessage[] = [],
): Record<string, IQuestionOption[]> => {
  const answersMap: Record<string, IQuestionOption[]> = {};
  for (const msg of messages) {
    if (msg.type !== 'user' || !msg.question?.category || !msg.response?.answer?.length) continue;
    const catId = msg.question.category;
    if (!answersMap[catId]) answersMap[catId] = [];
    answersMap[catId].push(...msg.response.answer);

    // Also index by frame (e.g. "catId.frame2") so per-frame dependency checks
    // use only that frame's answer and avoid cross-frame false positives.
    const frameMatch = msg.question.responsePath?.match(/frame(\d+)$/i);
    if (frameMatch?.[1]) {
      const frameKey = `${catId}.frame${frameMatch[1]}`;
      if (!answersMap[frameKey]) answersMap[frameKey] = [];
      answersMap[frameKey].push(...msg.response.answer);
    }
  }
  return answersMap;
};

/**
 * Resolve the frame width in mm from form values.
 * Tries overall_width, single width, then sum of multi-frame widths.
 */
export const resolveVentWidth = (formValues: Record<string, unknown>): number => {
  const overall = toNumber(formValues['overall_width_(mm)']);
  if (overall > 0) return overall;

  const single = toNumber(formValues['width_(mm)']);
  if (single > 0) return single;

  const frameWidths: number[] = [];
  for (let i = 1; i <= 5; i++) {
    const w = toNumber(formValues[`window_${i}_width_(mm)`]);
    if (w > 0) frameWidths.push(w);
  }
  if (frameWidths.length > 0) return frameWidths.reduce((a, b) => a + b, 0);

  return 0;
};

/**
 * Resolve the width relevant to a specific question for widthMin/widthMax checks.
 *
 * For per-frame questions (e.g. trickle vents for Frame 1) the `responsePath`
 * ends in `frame1`, `frame2`, etc. For those we use the specific frame's width
 * (`window_N_width_(mm)`) rather than the total couple/bay width, because a
 * 270 mm frame must fail `widthMin: 370` even when another frame in the same
 * window is wider.  Falls back to `resolveVentWidth` for non-frame-specific
 * questions.
 */
const resolveWidthForQuestion = (
  responsePath: string | undefined,
  formValues: Record<string, unknown>,
): number => {
  if (responsePath) {
    const m = responsePath.match(/frame(\d+)$/i);
    if (m) {
      const frameIdx = parseInt(m[1], 10);
      const frameW = toNumber(formValues[`window_${frameIdx}_width_(mm)`]);
      if (frameW > 0) return frameW;
    }
  }
  return resolveVentWidth(formValues);
};

/**
 * Resolve the answers to use when evaluating a dependency for a given category
 * and optional frame index.
 *
 * Problem: answers[catId.frameN] is non-empty once ANY frame-N question has been
 * answered, but it only contains answers from frame-N questions.  Product-level
 * answers (config mode, bar type, etc.) live in answers[catId] and are never
 * written into the frame key.  Using the frame key exclusively therefore misses
 * those product-level answers and causes per-frame questions to be hidden.
 *
 * Solution: for frame-specific questions combine
 *   (a) product-level answers — in answers[catId] but absent from ALL frame keys
 *   (b) the specific frame's answers — answers[catId.frameN]
 * This prevents cross-frame contamination while still exposing product-level deps.
 */
const resolveDepAnswers = (
  categoryId: string,
  frameIdx: string | undefined,
  answers: Record<string, IQuestionOption[]>,
): IQuestionOption[] => {
  if (!frameIdx) return answers[categoryId] ?? [];

  // Collect every optionId that was stored under any frame key for this category.
  const allFrameIds = new Set<string>(
    Object.keys(answers)
      .filter(k => k.startsWith(`${categoryId}.frame`))
      .flatMap(k => answers[k] ?? [])
      .map(o => o.optionId),
  );

  // Product-level = present in the category key but not in any per-frame key.
  const productLevel = (answers[categoryId] ?? []).filter(o => !allFrameIds.has(o.optionId));
  const frameSpecific = answers[`${categoryId}.frame${frameIdx}`] ?? [];

  return [...productLevel, ...frameSpecific];
};

/**
 * Check if an option should be displayed based on display conditions
 */
const shouldShowOption = (
  option: IQuestionOption,
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  formValues: Record<string, unknown> = {},
  questionResponsePath?: string,
): boolean => {
  const { displayCondition } = option;

  // D-Handle Position: hide "internal" option when BOTH Pole Eyes AND Both Sash Lifts are selected
  if (option.optionType === OPTION_TYPE_DHANDLE_POSITION) {
    const isInternalOption = option.responseValue?.toLowerCase() === "internal";
    if (isInternalOption) {

      // Check by iterating through all answers to find Hardware Item (18) or Hardware Type (61)
      let isPoleEyesSelected = false;
      let isBothSashLiftsSelected = false;
      const hardwareItems: any[] = [];

      // Loop through all answer arrays in the answers map
      for (const categoryAnswers of Object.values(answers)) {
        if (!Array.isArray(categoryAnswers)) continue;

        // Check if any answer in this category is for Hardware Item or Hardware Type
        for (const answer of categoryAnswers) {
          const isHardwareQuestion =
            answer.optionType === OPTION_TYPE_HARDWARE_ITEM ||
            answer.optionType === OPTION_TYPE_HARDWARE_TYPE;

          if (isHardwareQuestion) {
            hardwareItems.push({
              optionType: answer.optionType,
              optionText: answer.optionText,
              responseValue: answer.responseValue,
            });

            const isPoleEyes =
              answer.optionText?.toLowerCase().includes("pole eyes") ||
              answer.optionText?.toLowerCase().includes("pole eye") ||
              answer.responseValue?.toLowerCase().includes("pole eyes") ||
              answer.responseValue?.toLowerCase().includes("pole eye");

            const isBothSashLifts =
              answer.optionText?.toLowerCase().includes("sash lifts") ||
              answer.optionText?.toLowerCase().includes("sash lift") ||
              answer.responseValue?.toLowerCase().includes("sash lifts") ||
              answer.responseValue?.toLowerCase().includes("sash lift");

            if (isPoleEyes) {
              isPoleEyesSelected = true;
            }
            if (isBothSashLifts) {
              isBothSashLiftsSelected = true;
            }

            // Exit early if both are found
            if (isPoleEyesSelected && isBothSashLiftsSelected) {
              break;
            }
          }
        }

        if (isPoleEyesSelected && isBothSashLiftsSelected) break;
      }


      // Only hide internal option when BOTH Pole Eyes AND Both Sash Lifts are selected
      if (isPoleEyesSelected && isBothSashLiftsSelected) {
        return false;
      }
    }
  }

  // Arch-specific Profile Colour Type check.
  // Swept Arch: foil visibility driven by radius (from width + overall height + shoulder height).
  // True/Gothic Arch: foil visibility driven by width >= 800mm.
  if (
    selectedProduct?.producttypecode === ProductStructureType.Arch &&
    option.optionType === PROFILE_COLOR_OPTION
  ) {
    const width = resolveVentWidth(formValues);
    if (isSweptArchProduct(selectedProduct.optionText)) {
      if (isProfileColourFoilOption(option.responseValue ?? "")) {
        const overallHeight = toNumber(formValues["overall_height_(mm)"]);
        const shoulderHeight = toNumber(formValues["shoulder_height_(mm)"]);
        if (
          !shouldShowSweptArchFoilOption(width, overallHeight, shoulderHeight)
        )
          return false;
      }
    } else {
      if (!shouldShowArchProfileColourOption(option.responseValue ?? "", width))
        return false;
    }
  }

  // No condition = always show
  if (!displayCondition?.enabled) {
    return !displayCondition;
  }

  // Check product allowlist — collect all product UUIDs (WINDOW_TYPE + VARIATION) from the
  // current context so that validForProducts works regardless of which answer type stored the UUID.
  if (displayCondition.validForProducts?.length) {
    const productIds = collectAllProductIds(selectedProduct, answers);
    if (
      productIds.length > 0 &&
      !displayCondition.validForProducts.some((id) => productIds.includes(id))
    ) {
      return false;
    }
  }

  // Check width-based gating. `questionResponsePath` is forwarded from the
  // parent question so per-frame options use the specific frame's width.
  if (
    displayCondition.widthMin !== undefined ||
    displayCondition.widthMax !== undefined
  ) {
    const width = resolveWidthForQuestion(questionResponsePath, formValues);
    if (width > 0) {
      if (
        displayCondition.widthMin !== undefined &&
        width < displayCondition.widthMin
      )
        return false;
      if (
        displayCondition.widthMax !== undefined &&
        width > displayCondition.widthMax
      )
        return false;
    }
  }

  // Check dependencies
  if (displayCondition.dependsOn?.length) {
    const frameMatch = questionResponsePath?.match(/frame(\d+)$/i);
    const frameIdx = frameMatch?.[1];

    return displayCondition.dependsOn.every((dep) => {
      if (!dep.categoryId || !dep.optionIds?.length) return true;

      const answer = resolveDepAnswers(dep.categoryId, frameIdx, answers);
      if (!answer.length) return false;

      const selectedIds = answer.map((opt) => opt.optionId);
      return dep.optionIds.some((id) => selectedIds.includes(id));
    });
  }

  return true;
};;

/**
 * Resolve an option's conditional priceList against current answers.
 * When an entry's conditions all match (each condition's categoryId has at
 * least one matching selected optionId), overwrite `price` and `vssPrice` on
 * the option with the matched entry's values.
 * Entries later in the list win when multiple match.
 */
const resolveOptionPriceList = (
  option: IQuestionOption,
  answers: Record<string, IQuestionOption[]>,
): IQuestionOption => {
  if (!option.priceList?.length) return option;

  const isRuleMatch = (item: any): boolean => {
    if (!item.conditions?.length) return false;
    return item.conditions.every((cond: any) => {
      if (!cond.categoryId || !cond.optionIds?.length) return false;
      const selected = answers[cond.categoryId];
      if (!selected?.length) return false;
      const selectedIds = selected.map((o) => o.optionId);
      return cond.optionIds.some((id: string) => selectedIds.includes(id));
    });
  };

  // Pick the most specific matching rule: most AND-ed conditions wins.
  // When two rules have the same number of conditions the later one wins,
  // so admins can order fallbacks from most-specific to least-specific.
  let bestMatch: any = null;
  let bestMatchIndex = -1;
  option.priceList.forEach((item, idx) => {
    if (!isRuleMatch(item)) return;
    if (bestMatch === null) { bestMatch = item; bestMatchIndex = idx; return; }
    const itemScore = item.conditions?.length ?? 0;
    const bestScore = bestMatch.conditions?.length ?? 0;
    if (itemScore >= bestScore) { bestMatch = item; bestMatchIndex = idx; }
  });

  if (!bestMatch) return option;

  // Priority for the discount applied to a priceList-matched (e.g. Unglazed)
  // price:
  //   1. unglazedDiscountPercentage/Amount, when the config defines one.
  //   2. Else priceListDiscounts[bestMatchIndex] — looked up by the matched
  //      entry's OWN index into THIS SAME option's own priceList/
  //      priceListDiscounts arrays, so a discount authored for a different
  //      product's row can't leak onto this one via a coincidental index
  //      match (the original bug this exclusion was guarding against).
  //   3. Else no discount.
  const unglazedDiscountDefined =
    (option.unglazedDiscountPercentage ?? 0) > 0 || (option.unglazedDiscountAmount ?? 0) > 0;
  const plDiscount = option.priceListDiscounts?.find((d) => d.index === bestMatchIndex);
  const discountOverride = unglazedDiscountDefined
    ? { discountPercentage: option.unglazedDiscountPercentage ?? 0, discountAmount: option.unglazedDiscountAmount ?? 0 }
    : plDiscount
      ? { discountPercentage: plDiscount.discountPercentage ?? 0, discountAmount: plDiscount.discountAmount ?? 0 }
      : undefined;

  return {
    ...option,
    priceModifier: bestMatch.price !== 0 ? bestMatch.price : option.priceModifier,
    vssPrice: bestMatch.price !== 0 ? (bestMatch.vssPrice ?? option.vssPrice) : option.vssPrice,
    ...(discountOverride && {
      discountPercentage: discountOverride.discountPercentage,
      discountAmount: discountOverride.discountAmount,
    }),
  };
};


const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Horizontal run of the frame in metres (Head / Cill).
 * Field presence identifies the product shape — no hardcoded product IDs.
 */
/**
 * Returns the total coupling-clip deduction in mm for a coupled product.
 * For non-coupled products (or when selectedProduct is absent) returns 0.
 */
export const getCouplingClipDeductionMm = (selectedProduct?: IQuestionOption): number => {
  if (selectedProduct?.producttypecode !== ProductStructureType.Coupled) return 0;
  const frameCount = Math.max(1, selectedProduct.numberOfFrames ?? 1);
  return Math.max(0, frameCount - 1) * COUPLING_CLIP_WIDTH_MM;
};

const resolveHorizontalMetres = (
  formValues: Record<string, unknown>,
  selectedProduct?: IQuestionOption,
): number => {
  const deductionMm = getCouplingClipDeductionMm(selectedProduct);

  const overall = toNumber(formValues['overall_width_(mm)']);
  if (overall > 0) return (overall - deductionMm) / 1000;

  let sum = 0;
  for (let i = 1; i <= 5; i++) {
    sum += toNumber(formValues[`window_${i}_width_(mm)`]);
  }
  if (sum > 0) return Math.max(0, sum - deductionMm) / 1000;

  const width = toNumber(formValues['width_(mm)']);
  return width > 0 ? Math.max(0, width - deductionMm) / 1000 : 0;
};

/**
 * Vertical run of the frame in metres (Left / Right Jamb).
 * Arch uses shoulder height (jamb stops where the curve begins).
 */
const resolveVerticalMetres = (
  formValues: Record<string, unknown>,
): number => {
  const shoulder = toNumber(formValues['shoulder_height_(mm)']);
  if (shoulder > 0) return shoulder / 1000;
  const overall = toNumber(formValues['overall_height_(mm)']);
  return overall > 0 ? overall / 1000 : 0;
};

/**
 * Resolve the length (in metres) for a per-metre option based on which
 * component it represents — Head, Cill, Left Jamb, Right Jamb — and the
 * product's dimensions from the measurements form.
 *
 * The per-metre questions have an optionTypeName that cleanly identifies
 * the component ("Head Size", "Cill Size", "Left Jamb Size", "Right Jamb Size"),
 * so we read from there rather than from the option text.
 */
const resolvePerMeterLength = (
  option: IQuestionOption,
  question: IQuestion,
  formValues: Record<string, unknown>,
  selectedProduct?: IQuestionOption,
): number => {
  const typeName = (option.optionTypeName || question.optionTypeName || '').toLowerCase();
  if (typeName.includes('head') || typeName.includes('cill')) {
    return resolveHorizontalMetres(formValues, selectedProduct);
  }
  if (typeName.includes('jamb')) {
    return resolveVerticalMetres(formValues);
  }
  // Unknown per-metre component — leave multiplier at 1 so the rate shows as-is.
  return 1;
};

/**
 * Compute the per-sash price multiplier for an option based on:
 *  - Configuration mode (Individual vs Same For All) — from any prior answer
 *    with responseValue `individual_configuration` / `same_for_all_windows`
 *  - Placement (Top / Bottom / Both) — a prior answer in the same category
 *    whose responseValue is one of those, OR inferred from the question's
 *    optionTypeName when the question itself is side-specific
 *    (e.g. decorative bar "Top Sash" / "Bottom Sash" questions)
 *  - Selected product's numberOfSashes / numberOfFrames
 *
 * Returns 1 for non-per-sash options.
 */
const PER_UNIT_OPTION_UNIT = 4; // optionUnit 4 = Per Unit — price × entered numeric value

// Addon size optionTypes that are priced per metre of frame dimension.
// The config marks these as optionUnit 1 (Per Window) but they must be
// scaled by the actual width or height in metres at render time.
const ADDON_SIZE_OPTION_TYPES = new Set([50, 51, 52, 53]);

// Width threshold (mm) above which a hardware item requires 2 units per frame.
// Keyed by option.responseValue. Takes effect when option.widthQtyThreshold is
// not yet set in the Dataverse config — remove entries once the config carries the field.
const HARDWARE_WIDTH_QTY_THRESHOLDS: Record<string, number> = {
  "d-handle": 800,
  inset_finger_pull: 800,
  camlocks: 800,
  pole_eyes: 480,
  sash_lifts: 480,
};

const resolveOptionUnitMultiplier = (
  option: IQuestionOption,
  question: IQuestion,
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  formValues: Record<string, unknown> = {},
): number => {
  // Head / Cill / Jamb size options — always scale by frame dimension in metres.
  if (ADDON_SIZE_OPTION_TYPES.has(option.optionType)) {
    return resolvePerMeterLength(option, question, formValues, selectedProduct);
  }

  if (option.optionUnit === PER_METER_UNIT) {
    return resolvePerMeterLength(option, question, formValues, selectedProduct);
  }

  // Guard: if the product hasn't been selected yet (or reports 0 frames), default
  // to 1 so that applyUnitMultiplier never multiplies prices by 0 and zeroes them out.
  const frameCount = Math.max(1, selectedProduct?.numberOfFrames ?? 1);

  if (option.optionType === OPTION_TYPE_COUPLING_CLIP) {
    return Math.max(0, frameCount - 1);
  }

  // Per Unit (optionUnit 4): price × quantity is handled entirely by
  // calculatePriceFromMessages (getUnitMultiplier reads the form value).
  // Returning 1 here avoids double-counting the multiplier and ensures
  // the unit shown in the price breakdown matches the entered quantity.
  if (option.optionUnit === PER_UNIT_OPTION_UNIT) return 1;

  // Hardware items (optionType 18): qty per frame depends on each frame's width.
  // Threshold source: option.widthQtyThreshold (Dataverse config field) with a
  // built-in fallback map for well-known items (remove entries once the field is set in config).
  //
  // Two modes determined by whether the hardware category uses configuration mode:
  //  • Individual (responsePath ends in frameN): question is per-frame →
  //    read that specific frame's width and return 1 or 2.
  //  • Same-for-all / no config mode: question covers whole window →
  //    sum per-frame qtys across all frames.
  if (option.optionType === OPTION_TYPE_HARDWARE_ITEM) {
    const threshold =
      option.widthQtyThreshold ??
      HARDWARE_WIDTH_QTY_THRESHOLDS[option.responseValue];

    if (threshold !== undefined) {
      const frameMatch = question.responsePath?.match(/frame(\d+)$/i);

      if (frameMatch) {
        // Individual mode — per-frame question: use only this frame's width.
        const frameIdx = parseInt(frameMatch[1], 10);
        const frameWidth = toNumber(formValues[`window_${frameIdx}_width_(mm)`]);
        return frameWidth > 0 ? (frameWidth >= threshold ? 2 : 1) : 1;
      }

      // Same-for-all or single-frame product: whole-window question.
      // If individual frame widths are available (coupled/bay), sum per-frame qty.
      const frameWidths: number[] = [];
      for (let i = 1; i <= 5; i++) {
        const w = toNumber(formValues[`window_${i}_width_(mm)`]);
        if (w > 0) frameWidths.push(w);
      }
      if (frameWidths.length > 0) {
        return frameWidths.reduce((sum, fw) => sum + (fw >= threshold ? 2 : 1), 0);
      }

      // Single frame: use resolved overall width.
      const width = resolveVentWidth(formValues);
      return width > 0 ? (width >= threshold ? 2 : 1) : 1;
    }
    // No threshold configured → fall through to optionUnit logic.
  }

  if (option.optionUnit === PER_FRAME_UNIT) {
    const dependsOn = question.displayCondition?.dependsOn ?? [];
    // Scan dep categories for an explicit configuration-mode answer.
    // Only same_for_all_windows / individual_configuration count — other deps
    // (e.g. product/variation guards) must not suppress the frame multiplier.
    let configMode: "same_for_all" | "individual" | "none" = "none";
    for (const dep of dependsOn) {
      if (!dep.categoryId) continue;
      const catAnswers = answers[dep.categoryId] ?? [];
      if (catAnswers.some((o) => o.responseValue === "same_for_all_windows")) {
        configMode = "same_for_all";
        break;
      }
      if (
        catAnswers.some((o) => o.responseValue === "individual_configuration")
      ) {
        configMode = "individual";
        break;
      }
    }
    // No configuration-mode question in the flow → always multiply by frame count.
    return configMode === "individual" ? 1 : frameCount;
  }

  if (option.optionUnit !== PER_SASH_UNIT) return 1;

  const sashCount = selectedProduct?.numberOfSashes ?? 1;

  // Scope the config-mode check to the same category as the question.
  // Using all answers was a bug: a "Same for All Windows" answer in the
  // Sash category would incorrectly force perSide on Decorative Bar options
  // that were explicitly set to Individual Configuration.
  const categoryAnswers = answers[question.category] ?? [];
  const isSameForAll = categoryAnswers.some(
    (o) => o.responseValue === 'same_for_all_windows',
  );

  const placement = categoryAnswers
    .map((o) => o.responseValue)
    .find((v) => PLACEMENT_VALUES.has(v));

  const optTypeName = question.optionTypeName?.toLowerCase() ?? '';
  const questionIsSideSpecific =
    /\btop sash\b/.test(optTypeName) || /\bbottom sash\b/.test(optTypeName);

  // Same For All: scale across the whole product.
  // Top/bottom sash applies to 1 sash per frame × frameCount frames.
  // "Both" applies to all sashes across all frames.
  if (isSameForAll) {
    if (placement === 'both') return sashCount;
    if (placement === 'top' || placement === 'bottom') return frameCount;
    if (questionIsSideSpecific) return frameCount;
    return sashCount;
  }

  // Individual mode OR no mode in the flow (single-frame products like
  // Standard / Arch never present a Configuration Mode question).

  // Per-frame question in individual mode (responsePath ends in frameN).
  // Glass Specification price is per sash and each frame has 2 sashes → multiply by 2.
  // All other per-frame questions (top/bottom sash asked separately) are already per-sash → 1.
  if (/frame\d+$/i.test(question.responsePath ?? '')) {
    return question.optionType === OPTION_TYPE_GLASS_SPECIFICATION ? 2 : 1;
  }

  // Non-frame-specific questions: scale by placement or sash count.
  if (placement === 'both') return 2;
  if (placement === 'top' || placement === 'bottom') return 1;
  if (questionIsSideSpecific) return 1;
  // Single product-wide question with no placement and no top/bottom split
  // (e.g. Glass Specification) aplies to every sash in the product.
  return sashCount;
};

const applyUnitMultiplier = (
  option: IQuestionOption,
  multiplier: number,
): IQuestionOption => {
  if (multiplier === 1) return option;
  const scale = (v?: number | null) =>
    v === undefined || v === null ? v : v * multiplier;
  return {
    ...option,
    price: scale(option.price) ?? option.price,
    priceModifier: scale(option.priceModifier) ?? option.priceModifier,
    vssPrice: (scale(option.vssPrice) ?? option.vssPrice ?? 0) as number,
    // Discount fields must scale with the same multiplier as vssPrice so that
    // per-frame / per-sash / per-metre discounts match the scaled price.
    vssDiscountAmount: scale(option.vssDiscountAmount) ?? option.vssDiscountAmount,
    discountAmount: scale(option.discountAmount) ?? option.discountAmount,
  };
};

/**
 * Filter question options based on display conditions
 */
export const filterQuestionOptions = (
  question: IQuestion,
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  formValues: Record<string, unknown> = {},
): IQuestion => {
  // If no options, return question as is
  if (!question.options || question.options.length === 0) {
    return question;
  }

  // Filter options based on display conditions, resolve conditional priceList,
  // then pre-bake the unit multiplier (per-sash count or per-metre length)
  // into the option's price fields so rendered option cards show the true
  // total for the current selection.
  const filteredOptions = question.options
    .filter(
      (option) =>
        option != null && shouldShowOption(option, answers, selectedProduct, formValues, question.responsePath),
    )
    .map((option) => resolveOptionPriceList(option, answers))
    .map((option) =>
      applyUnitMultiplier(
        option,
        resolveOptionUnitMultiplier(
          option,
          question,
          answers,
          selectedProduct,
          formValues,
        ),
      ),
    );

  // Return question with filtered options
  return {
    ...question,
    options: filteredOptions,
  };
};;

/**
 * Check if a question should be displayed based on display conditions
 * ALL conditions must pass (AND logic) for the question to be shown
 */
export const shouldShowQuestion = (
  question: IQuestion,
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  allowMissingAnswers: boolean = false,
  formValues: Record<string, unknown> = {},
): boolean => {
  const { displayCondition, optionType } = question;

  // Questions never rendered in the chat flow — skip and advance to next.
  if (optionType === OPTION_TYPE_COUPLING_CLIP) return false;
  if (optionType === OPTION_TYPE_FIXING_LUGS) return false;
  if (optionType === OPTION_TYPE_FITTING_TYPE) return false;

  // If Hardware Required (91) was explicitly answered "No", hide all hardware questions
  // regardless of their display condition configuration.
  const HARDWARE_OPTION_TYPES = [9, 18, 61, 76];
  if (HARDWARE_OPTION_TYPES.includes(optionType)) {
    const hardwareRequiredNo = Object.values(answers).some(opts =>
      opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'no')
    );
    if (hardwareRequiredNo) return false;
  }

  // No condition or disabled = don't show
  if (!displayCondition?.enabled) {
    return !displayCondition; // Show only if no condition exists
  }

  // Check 0: Width-based gating on the question itself (mirrors the same check
  // applied to individual options in shouldShowOption). Use the frame-aware
  // resolver so per-frame questions (e.g. trickle vents "Frame 1") compare
  // against their specific frame's width rather than the total couple width.
  if (displayCondition.widthMin !== undefined || displayCondition.widthMax !== undefined) {
    const width = resolveWidthForQuestion(question.responsePath, formValues);
    if (width > 0) {
      if (displayCondition.widthMin !== undefined && width < displayCondition.widthMin) return false;
      if (displayCondition.widthMax !== undefined && width > displayCondition.widthMax) return false;
    }
  }

  // Arch-specific: Spray (optionType 10) is hidden when the frame width is
  // below the Foil minimum (800 mm) because Spray is only applicable alongside
  // Foil profile colour. Standard White arch windows don't receive a spray
  // colour, so showing the spray question for narrow arches is incorrect.
  if (
    optionType === OPTION_TYPE_SPRAY &&
    selectedProduct?.producttypecode === ProductStructureType.Arch
  ) {
    const width = resolveVentWidth(formValues);
    if (width > 0 && width < ARCH_FOIL_COLOUR_WIDTH_MIN) return false;
  }

  // Angled Cill Horn (optionType 73): only show for bay windows when the cill
  // horn size entered by the user is 100 mm or greater.
  // The key is resolved dynamically from the answered options (optionType 77)
  // so it stays correct if the responseValue in the config ever changes.
  if (optionType === OPTION_TYPE_ANGLED_CILL_HORN) {
    if (selectedProduct?.producttypecode !== ProductStructureType.Bays) return false;
    const cillHornSizeOption = Object.values(answers).flat()
      .find(o => o.optionType === OPTION_TYPE_CILL_HORN_SIZE);
    const hornSize = cillHornSizeOption
      ? Number(formValues[cillHornSizeOption.responseValue ?? ''])
      : 0;
    if (!hornSize || hornSize < CILL_HORN_MIN_FOR_ANGLED) return false;
  }

  // Check 1: Product validation (different logic for VARIATION vs other types)
  if (displayCondition.validForProducts?.length) {
    if (optionType === OPTION_TYPE_VARIATION) {
      // For VARIATION questions: Check if validProducts from answers intersect with question's validForProducts
      const validProductsFromAnswers = getValidProductsFromAnswers(answers);
      if (validProductsFromAnswers.length > 0) {
        const hasMatch = hasIntersection(
          validProductsFromAnswers,
          displayCondition.validForProducts,
        );
        if (!hasMatch) {
          return false; // Fail: No intersection found
        }
      }
    } else {
      // For non-VARIATION questions: Check all product UUIDs in context against validForProducts
      const productIds = collectAllProductIds(selectedProduct, answers);
      if (productIds.length > 0 && !displayCondition.validForProducts.some(id => productIds.includes(id))) {
        return false; // Fail: no known product matches validForProducts
      }
    }
  }

  // Check 2: Dependencies - ALL dependencies must be satisfied
  if (displayCondition.dependsOn?.length) {
    const frameMatch = question.responsePath?.match(/frame(\d+)$/i);
    const frameIdx = frameMatch?.[1];

    const allDependenciesMet = displayCondition.dependsOn.every((dep) => {
      if (!dep.categoryId || !dep.optionIds?.length) return true;

      const answer = resolveDepAnswers(dep.categoryId, frameIdx, answers);
      if (!answer.length) {
        return allowMissingAnswers ? true : false;
      }

      const selectedIds = answer.map((opt) => opt.optionId);
      return dep.optionIds.some((id) => selectedIds.includes(id));
    });

    if (!allDependenciesMet) {
      // Hardware type override: if user said Yes to Hardware Required, bypass
      // option-based conditions (e.g. fixed-sash hiding hardware). But do NOT
      // override when the failure is a config-mode dep (individual/same-for-all) —
      // per-frame questions must still respect whichever mode the user chose.
      if (optionType === OPTION_TYPE_HARDWARE_TYPE) {
        const hardwareRequiredYes = Object.values(answers).some((opts) =>
          opts.some(
            (o) =>
              o.optionType === OPTION_TYPE_HARDWARE_REQUIRED &&
              o.responseValue === "yes",
          ),
        );
        if (hardwareRequiredYes) {
          const failingDueToConfigMode = displayCondition.dependsOn.some(
            (dep) => {
              if (!dep.categoryId || !dep.optionIds?.length) return false;
              const ans = resolveDepAnswers(dep.categoryId, frameIdx, answers);
              const depMet = dep.optionIds.some((id) =>
                ans.map((o) => o.optionId).includes(id),
              );
              if (depMet) return false;
              return ans.some(
                (o) =>
                  o.responseValue === "individual_configuration" ||
                  o.responseValue === "same_for_all_windows",
              );
            },
          );
          if (!failingDueToConfigMode) return true;
        }
      }
      return false;
    }
  }

  // All checks passed
  return true;
};

/**
 * Returns true when at least one option in a question passes shouldShowOption.
 * Questions with no options (free-text, etc.) are always considered visible.
 */
export const hasVisibleOptions = (
  question: IQuestion,
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  formValues: Record<string, unknown> = {},
): boolean => {
  if (!question.options?.length) return true;
  return question.options.some(
    (option) => option != null && shouldShowOption(option, answers, selectedProduct, formValues, question.responsePath),
  );
};

/**
 * Get the next visible question that meets display conditions
 */
export const getNextVisibleQuestion = (
  currentQuestionId: string,
  questions: IQuestion[],
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  allowMissingAnswers: boolean = false,
  formValues: Record<string, unknown> = {},
): IQuestion | null => {
  const currentIndex = questions.findIndex(q => q.questionId === currentQuestionId);
  const startIndex = currentIndex === -1 ? 0 : currentIndex + 1;

  for (let i = startIndex; i < questions.length; i++) {
    const q = questions[i];
    if (
      q &&
      shouldShowQuestion(q, answers, selectedProduct, allowMissingAnswers, formValues) &&
      hasVisibleOptions(q, answers, selectedProduct, formValues)
    ) {
      return q;
    }
  }

  return null;
};

export const getSkipQuestionIdsQuestion = (
  currentQuestionId: string,
  questions: IQuestion[],
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  allowMissingAnswers: boolean = false,
  formValues: Record<string, unknown> = {},
): string[] => {
  const currentIndex = questions.findIndex(
    (q) => q.questionId === currentQuestionId,
  );
  const startIndex = currentIndex === -1 ? 0 : currentIndex + 1;
  const skipQuestionsList = [];
  for (let i = startIndex; i < questions.length; i++) {
    if (!questions[i]) continue;
    if (
      shouldShowQuestion(questions[i], answers, selectedProduct, allowMissingAnswers, formValues) &&
      hasVisibleOptions(questions[i], answers, selectedProduct, formValues)
    ) {
      break;
    } else {
      skipQuestionsList.push(questions[i].questionId);
    }
  }

  return skipQuestionsList;
};

/**
 * Find all questions that depend on a changed category/option or selected product
 */
export const getDependentQuestions = (
  questionFlow: IQuestion[],
  changedCategory: string,
  selectedOption: IQuestionOption,
  selectedProduct?: IQuestionOption
): IQuestion[] => {
  const dependentQuestions: IQuestion[] = [];

  questionFlow.forEach(q => {
    if (!q || !q.displayCondition?.enabled) return;

    let isDependentOnOption = false;
    let isDependentOnProduct = false;

    // Check if question depends on the changed category and selected option
    if (q.displayCondition.dependsOn && q.displayCondition.dependsOn.length > 0) {
      q.displayCondition.dependsOn.forEach(dep => {
        // Check if depends on the changed category
        if (dep.categoryId === changedCategory) {
          // Check if depends on the specific option selected
          if (!dep.optionIds || dep.optionIds.length === 0 || dep.optionIds.includes(selectedOption.optionId)) {
            isDependentOnOption = true;
          }
        }
      });
    }

    // Check if question is valid for the selected product
    if (q.displayCondition.validForProducts && q.displayCondition.validForProducts.length > 0 && selectedProduct) {
      if (q.displayCondition.validForProducts.includes(selectedProduct.optionId)) {
        isDependentOnProduct = true;
      }
    }

    // Add to dependent questions if it matches conditions
    if (isDependentOnOption || isDependentOnProduct) {
      dependentQuestions.push(q);
    }
  });

  return dependentQuestions;
};


export const getNextVisibleDependentQuestion = (
  currentQuestionId: string,
  questions: IQuestion[],
  answers: Record<string, IQuestionOption[]>,
  isProductChanged: boolean,
  selectedProduct?: IQuestionOption,
): IQuestion[] => {
  const currentIndex = questions.findIndex(
    (q) => q.questionId === currentQuestionId,
  );
  const startIndex = currentIndex === -1 ? 0 : currentIndex + 1;
  const questionsList: IQuestion[] = [];
  // Find next question that should be shown
  for (let i = startIndex; i < questions.length; i++) {
    if (!questions[i]) continue;
    if (
      shouldShowDependentQuestion(
        questions[i],
        answers,
        isProductChanged,
        selectedProduct,
      )
    ) {
      questionsList.push(questions[i]);
    }
  }
  return questionsList;
};

/**
 * Find questions further down the flow that depend on a form field that was
 * just submitted in `formQuestionId`.
 *
 * Form questions are different from regular single/multi-select questions:
 * each *option* on the question represents a form field, and the user fills
 * in a value rather than choosing the option. `getNextVisibleDependentQuestion`
 * resolves dependencies by matching `dep.optionIds` against the optionId of
 * answer entries — for form submissions this can fail when the per-field
 * answer entries fall back to using the responseValue (string key) as their
 * optionId rather than the field's GUID.
 *
 * This helper resolves the form-field dependency robustly by:
 *   1. Looking up each submitted form key against the form question's options
 *      to recover both the field's GUID and its responseValue.
 *   2. Scanning subsequent questions for any whose `dependsOn` entry references
 *      the form question's category and any of those identifiers.
 *
 * Only fields whose values actually changed compared to `originalFormValues`
 * count as triggers. This avoids re-asking the dependent question when the
 * user re-submits the form without modifying the field that drives it (e.g.
 * editing Width but not Overall Height should leave Transom Drop alone if
 * Transom Drop only depends on Overall Height).
 *
 * The returned list is intended to be merged with the regular dependent-question
 * list so that downstream questions tied to a form field (e.g. Transom Drop
 * depending on Overall Height) are surfaced after the form is submitted.
 */
export const getFormFieldDependentQuestions = (
  formQuestionId: string,
  questionFlow: IQuestion[],
  formValues: Record<string, unknown>,
): IQuestion[] => {
  const formQuestion = questionFlow.find((q) => q.questionId === formQuestionId);
  if (!formQuestion) return [];

  // Every currently-filled field is always treated as a trigger, whether or not
  // its value actually changed from a prior submission. A presence-based
  // dependent (e.g. Transom Drop depending on "was Overall Height answered at
  // all", not a value/threshold) must always be re-surfaced right after the
  // form is (re)submitted — in the normal (non-edit) flow it's unconditionally
  // the next question the moment the field has any value, so edit mode must
  // match that rather than gating on a value diff.
  const submittedFieldIds = new Set<string>();
  Object.keys(formValues).forEach((key) => {
    const value = formValues[key];
    if (value === null || value === undefined || value === '') return;

    submittedFieldIds.add(key);
    const opt = formQuestion.options?.find(
      (o) => o.responseValue === key || o.optionId === key,
    );
    if (opt?.optionId) submittedFieldIds.add(opt.optionId);
    if (opt?.responseValue) submittedFieldIds.add(opt.responseValue);
  });

  if (submittedFieldIds.size === 0) return [];

  const currentIdx = questionFlow.findIndex((q) => q.questionId === formQuestionId);
  if (currentIdx === -1) return [];

  const dependents: IQuestion[] = [];
  for (let i = currentIdx + 1; i < questionFlow.length; i++) {
    const q = questionFlow[i];
    const deps = q?.displayCondition?.dependsOn;
    if (!deps?.length) continue;
    const matches = deps.some(
      (dep) =>
        dep.categoryId === formQuestion.category &&
        dep.optionIds?.some((id) => submittedFieldIds.has(id)),
    );
    if (matches) dependents.push(q);
  }
  return dependents;
};



/**
 * Check if a question is dependent (has display conditions)
 * Returns true if the question depends on the provided answers and is valid for the product
 */
export const shouldShowDependentQuestion = (
  question: IQuestion,
  answers: Record<string, IQuestionOption[]>,
  isProductChanged: boolean,
  selectedProduct?: IQuestionOption,
): boolean => {
  if (!question) return false;
  const { displayCondition, optionType } = question;
  // Check 1: Product validation (different logic for VARIATION vs other types)
  if (displayCondition?.validForProducts?.length) {
    if (optionType === OPTION_TYPE_VARIATION) {
      // For VARIATION questions: Check if validProducts from answers intersect with question's validForProducts
      const validProductsFromAnswers = getValidProductsFromAnswers(answers);
      if (validProductsFromAnswers.length > 0) {
        const hasMatch = hasIntersection(
          validProductsFromAnswers,
          displayCondition.validForProducts,
        );

        if (!hasMatch) {
          return false; // Fail: No intersection found
        } else if (isProductChanged) {
          return true;
        }
      }
    } else {
      // For non-VARIATION questions: Check all product UUIDs in context against validForProducts
      const productIds = collectAllProductIds(selectedProduct, answers);
      if (productIds.length > 0) {
        if (!displayCondition.validForProducts.some(id => productIds.includes(id))) {
          return false; // Fail: no known product matches validForProducts
        } else if (isProductChanged) {
          return true;
        }
      }
    }
  }

  // Hardware type override: mirrors shouldShowQuestion — when hardware_required = yes,
  // force hardware type into the dependent list even though its display condition
  // doesn't include a dependsOn entry for the hardware_required question.
  if (optionType === OPTION_TYPE_HARDWARE_TYPE) {
    const hardwareRequiredYes = Object.values(answers).some(opts =>
      opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'yes')
    );
    if (hardwareRequiredYes) return true;
  }

  // Check 2: Dependencies - check if ANY dependency is satisfied by the provided answers
  if (displayCondition?.dependsOn?.length) {
    const hasMatchingDependency = displayCondition.dependsOn.some((dep) => {
      if (!dep.categoryId || !dep.optionIds?.length) return false;

      const answer = answers[dep.categoryId];
      if (!answer?.length) return false; // No answer for this category

      const selectedIds = answer.map((opt) => opt.optionId);
      // Check if any of the selected options match any of the dependency optionIds
      return dep.optionIds.some((id) => selectedIds.includes(id));
    });

    if (hasMatchingDependency) {
      return true; // Question depends on at least one of the provided answers
    }
  }

  // No matching dependencies found
  return false;
};

/** Info about a trickle-vent question skipped because its frame is too narrow/wide. */
export interface TrickleVentSkipInfo {
  questionId: string;
  /** Human-readable label, e.g. "Frame 1" */
  frameLabel: string;
  frameWidth: number;
  widthMin?: number;
  widthMax?: number;
}

/**
 * Return info for every trickle-vent question in the flow that is currently
 * hidden *because of a width constraint* (widthMin/widthMax on its own
 * displayCondition) AND whose product/dependency checks would otherwise pass.
 *
 * This is used to inject a friendly "Trickle vents unavailable for Frame N"
 * info alert so the user understands why the question was skipped.
 *
 * @param trickleVentOptionType – optionType value for Trickle Vents (40)
 */
export const getSkippedTrickleVentFrames = (
  questionFlow: IQuestion[],
  formValues: Record<string, unknown>,
  answers: Record<string, IQuestionOption[]>,
  selectedProduct?: IQuestionOption,
  trickleVentOptionType = 40,
): TrickleVentSkipInfo[] => {
  const result: TrickleVentSkipInfo[] = [];

  for (const q of questionFlow) {
    if (q.optionType !== trickleVentOptionType) continue;
    if (!q.displayCondition?.enabled) continue;

    const { widthMin, widthMax } = q.displayCondition;
    if (widthMin === undefined && widthMax === undefined) continue;

    const frameWidth = resolveWidthForQuestion(q.responsePath, formValues);
    if (frameWidth <= 0) continue; // no width known yet — skip

    const failsWidth =
      (widthMin !== undefined && frameWidth < widthMin) ||
      (widthMax !== undefined && frameWidth > widthMax);
    if (!failsWidth) continue;

    // Only alert when width is the reason — not when product / dependency checks already hide it.
    // Check product allowlist: if validForProducts is set and the current product doesn't match,
    // the question is hidden for product reasons and we don't need to explain width.
    if (q.displayCondition.validForProducts?.length) {
      const productIds = collectAllProductIds(selectedProduct, answers);
      if (productIds.length > 0 && !q.displayCondition.validForProducts.some(id => productIds.includes(id))) {
        continue; // hidden for product reasons — no width alert
      }
    }

    // Extract frame label from responsePath ("frame1" → "Frame 1") or question text
    const pathMatch = q.responsePath?.match(/frame(\d+)$/i);
    const textMatch = !pathMatch ? q.question.match(/(?:frame|window)\s+(\d+)/i) : null;
    const match = pathMatch ?? textMatch;
    const frameLabel = match ? `Frame ${match[1]}` : 'your window';

    result.push({ questionId: q.questionId, frameLabel, frameWidth, widthMin, widthMax });
  }

  return result;
};
